import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator, Minus, Plus, MessageCircle, RotateCcw } from "lucide-react";
import { SIMULATOR_SERVICES, waLink } from "../data/content";

const MAX_QTY = { "unid.": 20, "m²": 500 };

export const QuoteSimulator = () => {
  const [qty, setQty] = useState({});

  const bump = (id, step, unit, delta) =>
    setQty((q) => {
      const next = Math.max(0, Math.min(MAX_QTY[unit], (q[id] || 0) + delta * step));
      return { ...q, [id]: next };
    });

  const { lines, total } = useMemo(() => {
    const lines = [];
    let total = 0;
    SIMULATOR_SERVICES.forEach((group) =>
      group.items.forEach((item) => {
        const q = qty[item.id] || 0;
        if (q > 0) {
          const subtotal = q * item.price;
          total += subtotal;
          lines.push({ ...item, q, subtotal });
        }
      })
    );
    return { lines, total };
  }, [qty]);

  const message =
    "Olá! Gostaria de pedir um orçamento:\n" +
    lines.map((l) => `• ${l.label} ×${l.q} ${l.unit} — ${l.subtotal} €`).join("\n") +
    `\nTotal estimado: ${total} €`;

  return (
    <section id="simulador" data-testid="simulator-section" className="py-20 sm:py-28 bg-[#0A1128]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-sky-400/30 bg-sky-400/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-sky-300">
            <Calculator className="h-3.5 w-3.5" />
            Simulador de Orçamento
          </span>
          <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            Calcule a sua estimativa em segundos
          </h2>
          <p className="mt-4 text-base text-slate-400 leading-relaxed">
            Escolha os serviços e as quantidades, veja o total estimado e envie
            tudo diretamente para o nosso WhatsApp.
          </p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-5 gap-8 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-3 space-y-8"
          >
            {SIMULATOR_SERVICES.map((group) => (
              <div key={group.category}>
                <h3 className="font-display text-sm font-bold uppercase tracking-widest text-sky-400 mb-3">
                  {group.category}
                </h3>
                <div className="space-y-2.5">
                  {group.items.map((item) => {
                    const q = qty[item.id] || 0;
                    return (
                      <div
                        key={item.id}
                        data-testid={`sim-item-${item.id}`}
                        className={`flex items-center justify-between gap-4 rounded-2xl border px-5 py-3.5 transition-colors ${
                          q > 0
                            ? "border-sky-400/50 bg-sky-400/10"
                            : "border-white/10 bg-[#101D42]"
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm sm:text-[15px] font-medium text-slate-100 truncate">
                            {item.label}
                          </p>
                          <p className="text-xs text-slate-400">
                            {item.price} € / {item.unit}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <button
                            data-testid={`sim-minus-${item.id}`}
                            onClick={() => bump(item.id, item.step, item.unit, -1)}
                            disabled={q === 0}
                            aria-label={`Diminuir ${item.label}`}
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-slate-300 hover:border-sky-400/50 hover:text-sky-300 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span
                            data-testid={`sim-qty-${item.id}`}
                            className="w-14 text-center font-display text-base font-bold text-white"
                          >
                            {q}
                            <span className="ml-1 text-[10px] font-medium text-slate-400">{item.unit}</span>
                          </span>
                          <button
                            data-testid={`sim-plus-${item.id}`}
                            onClick={() => bump(item.id, item.step, item.unit, 1)}
                            aria-label={`Aumentar ${item.label}`}
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-500/20 border border-sky-400/40 text-sky-300 hover:bg-sky-500/35 transition-colors"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="sim-summary"
            className="lg:col-span-2 lg:sticky lg:top-28 rounded-3xl border border-sky-400/25 bg-gradient-to-b from-[#162550] to-[#101D42] p-7"
          >
            <h3 className="font-display text-lg font-bold text-white">O seu orçamento</h3>

            {lines.length === 0 ? (
              <p data-testid="sim-empty" className="mt-4 text-sm text-slate-400 leading-relaxed">
                Adicione serviços ao lado para ver a estimativa total.
              </p>
            ) : (
              <ul className="mt-4 space-y-2.5" data-testid="sim-lines">
                {lines.map((l) => (
                  <li key={l.id} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-slate-300 truncate">
                      {l.label} <span className="text-slate-500">×{l.q} {l.unit}</span>
                    </span>
                    <span className="font-semibold text-sky-300 whitespace-nowrap">{l.subtotal} €</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-6 border-t border-white/10 pt-5 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-300">Total estimado</span>
              <span data-testid="sim-total" className="font-display text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-cyan-300">
                {total} €
              </span>
            </div>

            <a
              href={total > 0 ? waLink(message) : undefined}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sim-whatsapp-button"
              aria-disabled={total === 0}
              className={`mt-6 flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition-colors ${
                total > 0
                  ? "bg-[#25D366] hover:bg-[#1eb857] text-[#06281a] shadow-lg shadow-[#25D366]/25"
                  : "bg-white/10 text-slate-500 pointer-events-none"
              }`}
            >
              <MessageCircle className="h-4 w-4" />
              Enviar Orçamento no WhatsApp
            </a>

            <button
              data-testid="sim-reset-button"
              onClick={() => setQty({})}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-white/10 px-6 py-2.5 text-xs font-semibold text-slate-400 hover:text-sky-300 hover:border-sky-400/40 transition-colors"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Limpar seleção
            </button>

            <p className="mt-5 text-[11px] text-slate-500 leading-relaxed">
              Valor estimado pela tabela de preços. Confirmamos o orçamento
              final no WhatsApp, sem compromisso.
            </p>
          </motion.aside>
        </div>
      </div>
    </section>
  );
};
